import type { HarmonicAssessmentResponse } from "../services/harmonics";
import { formatQuantity } from "../utils/formatQuantity";
import { STATUS_TONE, statusHeading } from "./PfCorrectionResultSummary";
import { StatusIcon } from "./StatusIcon";
import { bannerTone } from "./statusTone";
import { COMPARISON_LABELS, LIMIT_BASIS_LABELS } from "./harmonicsLabels";

type HarmonicResultSummaryProps = {
  result: HarmonicAssessmentResponse;
};

// The decisive values first (Master Prompt v2.1 section 19): THDi and TDD of the current,
// THDv at the point of common coupling, Isc / IL, and the basis the limits were judged on with
// each comparison's outcome. No figure is calculated here: every value is the engine's.
// Display rule A12: four significant figures on screen, the exact engine decimal in the
// tooltip (title).
export function HarmonicResultSummary({ result }: HarmonicResultSummaryProps) {
  const tone = STATUS_TONE[result.status];

  const thdi = formatQuantity(result.thdi_percent, "%");
  const tdd = formatQuantity(result.tdd_percent, "%");
  const thdv = formatQuantity(result.thdv_percent, "%");
  const iscIl = formatQuantity(result.isc_il_ratio, "");
  const thdvLimit = formatQuantity(result.thdv_limit_percent, "%");
  const tddLimit = formatQuantity(result.tdd_limit_percent, "%");

  return (
    <section aria-label="Result summary" data-summary-tone={tone}>
      <header>
        <p data-summary-status={result.status}>
          <StatusIcon tone={bannerTone(result.status, STATUS_TONE)} size={20} />
          {statusHeading(result.status)}
        </p>
        <p>
          Study <strong>{result.code}</strong> · {result.name} · {result.jurisdiction_profile}{" "}
          profile
        </p>
      </header>
      <dl data-summary-strip>
        <div>
          <dt>THDi</dt>
          {/* The harmonic current over the fundamental of the loads. */}
          <dd data-summary-field="thdi" title={thdi.exact ?? undefined}>
            {thdi.display}
          </dd>
        </div>
        <div>
          <dt>TDD</dt>
          {/* Over the maximum demand current IL; empty when IL was not established. */}
          <dd
            data-summary-field="tdd"
            data-summary-value="text"
            data-comparison={result.tdd_comparison}
            title={[tdd.exact, tddLimit.exact].filter(Boolean).join(" · ") || undefined}
          >
            {tdd.display}
            {result.tdd_limit_percent !== null ? ` (limit ${tddLimit.display})` : ""}
            {result.tdd_comparison === "NOT_EVALUATED" ? "" : ` · ${COMPARISON_LABELS[result.tdd_comparison]}`}
          </dd>
        </div>
        <div>
          <dt>THDv</dt>
          {/* From the harmonic currents and the source impedance per order. */}
          <dd
            data-summary-field="thdv"
            data-summary-value="text"
            data-comparison={result.thdv_comparison}
            title={[thdv.exact, thdvLimit.exact].filter(Boolean).join(" · ") || undefined}
          >
            {thdv.display}
            {result.thdv_limit_percent !== null ? ` (limit ${thdvLimit.display})` : ""}
            {result.thdv_comparison === "NOT_EVALUATED" ? "" : ` · ${COMPARISON_LABELS[result.thdv_comparison]}`}
          </dd>
        </div>
        <div>
          <dt>Isc / IL</dt>
          <dd data-summary-field="isc-il" title={iscIl.exact ?? undefined}>
            {iscIl.display.trim()}
          </dd>
        </div>
        <div>
          <dt>Limit basis</dt>
          <dd data-summary-field="limit-basis" data-summary-value="text" data-limit-basis={result.limit_basis}>
            {LIMIT_BASIS_LABELS[result.limit_basis]}
          </dd>
        </div>
        <div>
          <dt>Warnings</dt>
          <dd data-summary-warnings={result.warnings.length}>{result.warnings.length}</dd>
        </div>
      </dl>
    </section>
  );
}

export default HarmonicResultSummary;
